<script setup>
const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
  layout: {
    type: String,
  },
  bgColor: {
    type: String,
  },
  title: {
    type: String,
  },
  body: {
    type: String,
  },
  linkList: {
    type: Array,
  },
})

// Computed properties to handle both model and individual props
const bgColorValue = computed(() => props.model?.bgColor?.value || props.bgColor)
const layoutValue = computed(() => props.model?.layout?.value || props.layout)
const titleValue = computed(() => props.model?.title?.value || props.title)
const bodyValue = computed(() => props.model?.body?.value || props.body)
const linkListValue = computed(() => props.model?.linkList?.value || props.linkList)
</script>

<template>
  <div
    class="file-listing-block block-spacing"
    :class="{
      'file-listing-block--split': layoutValue === 'split',
      'file-listing-block--aqua-bg': bgColorValue === 'aqua',
      'file-listing-block--navy-bg': bgColorValue === 'navy',
    }"
  >
    <div class="container-fluid">
      <div
        class="file-listing-block__wrap"
        :class="{
          'bg-med-light-aqua card-shadow rounded-xl': bgColorValue === 'aqua',
          'dark-bg bg-primary card-shadow rounded-xl': bgColorValue === 'navy',
          'max-w-[1194px] mx-auto lg:grid lg:grid-cols-10 lg:gap-[30px]': layoutValue === 'split',
        }"
      >
        <BlockHeader
          :title="titleValue"
          :summary="bodyValue"
          :class="{
            'lg:col-span-4': layoutValue === 'split',
          }"
        />
        <div
          class="file-listing-block__items-wrap"
          :class="{
            'lg:col-span-6': layoutValue === 'split',
          }"
        >
          <div
            v-for="(item, index) in linkListValue"
            :key="index"
            class="file-listing-block__item prose"
          >
            <div>
              <ULink
                v-if="item.href && item.text"
                class="text-primary hover:text-primary"
                :href="useCmsUrl(item.href)"
                :locale="false"
              >
                {{ item.text }}
              </ULink>
              <p
                v-if="item.title"
                class="file-listing-block__title"
              >
                {{ item.title }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.file-listing-block {
  &:not(.file-listing-block--split) {
    .file-listing-block__wrap {
      max-width: var(--text-width);
      margin-right: auto;
      margin-left: auto;
    }

    .block-header {
      border-bottom: 2px solid var(--color-med-aqua);
    }
  }

  .file-listing-block__wrap {
    .block-header {
      .block-header__summary {
        font-size: 15px;

        p {
          font-size: inherit;
          color: inherit;
        }
      }

      @media (min-width: 1600px) {
        max-width: unset;
      }
    }

    &:not(.dark-bg) {
      .block-header {
        .block-header__summary {
          color: var(--color-med-navy);
        }
      }

      .file-listing-block__item {
        .file-listing-block__title {
          color: var(--color-med-navy);
        }
      }
    }
  }
}

.file-listing-block--aqua-bg,
.file-listing-block--navy-bg {
  .file-listing-block__wrap {
    padding: 40px 30px 45px;

    @media (min-width: 992px) {
      padding: 40px 55px 55px;
    }
  }

  &.file-listing-block--split {
    .file-listing-block__wrap {
      @media (min-width: 1400px) {
        padding: 50px 60px 60px;
      }
    }
  }

  .file-listing-block__item {
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
      margin-bottom: 0;
    }
  }
}

.file-listing-block__item {
  border-bottom: 2px solid var(--color-med-aqua);
  padding-bottom: 20px;
  margin-bottom: 20px;
  font-size: 18px;

  .file-listing-block__title {
    font-size: 15px;
  }

  @media (min-width: 992px) {
    padding-bottom: 30px;
    margin-bottom: 30px;
  }
  > * {
    &:last-child {
      margin-bottom: 0;
    }
  }

  h3 {
    text-wrap: pretty;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  a[href^='http']:not([href*='180medical.com']):not([href$='.pdf']) {
    &::after {
      content: '';
      width: 24px;
      height: 24px;
      display: inline-block;
      flex-shrink: 0;
      background-color: currentColor;

      mask: url('/images/icons/external-link.svg') no-repeat center;
      -webkit-mask: url('/images/icons/external-link.svg') no-repeat center;

      mask-size: contain;
      -webkit-mask-size: contain;
    }
  }

  // PDF links
  a[href$='.pdf'] {
    &::after {
      content: '';
      width: 24px;
      height: 24px;
      display: inline-block;
      flex-shrink: 0;
      background-color: currentColor;

      mask: url('/images/icons/file-pdf.svg') no-repeat center;
      -webkit-mask: url('/images/icons/file-pdf.svg') no-repeat center;

      mask-size: contain;
      -webkit-mask-size: contain;
    }
  }
}

.dark-bg {
  .block-header__summary {
    color: white;

    p {
      font-size: inherit;
      color: inherit;
    }
  }
}
</style>
